/**
 * Guide Selection Screen
 *
 * Shown during onboarding and accessible from Settings.
 * User swipes/clicks through characters and selects one.
 *
 * Australian English throughout.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Sparkles } from 'lucide-react';
import { useGuide } from '../../context/GuideContext';

const GuideSelection = ({ onComplete, isOnboarding = true }) => {
  const { allCharacters, characterOrder, selectGuide, selectedGuideId } = useGuide();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);

  const currentCharacter = allCharacters[characterOrder[currentIndex]];

  const handleSelect = async () => {
    setIsSelecting(true);

    // Small delay for visual feedback
    await new Promise(resolve => setTimeout(resolve, 300));

    const success = selectGuide(currentCharacter.id);

    if (success) {
      onComplete?.(currentCharacter);
    }

    setIsSelecting(false);
  };

  const goNext = () => {
    setCurrentIndex((i) => (i + 1) % characterOrder.length);
  };

  const goPrev = () => {
    setCurrentIndex((i) => (i - 1 + characterOrder.length) % characterOrder.length);
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowLeft') goPrev();
    if (e.key === 'ArrowRight') goNext();
    if (e.key === 'Enter') handleSelect();
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col items-center justify-center p-6"
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="w-6 h-6 text-purple-400" />
          <h1 className="text-2xl font-bold text-white">
            Choose Your Guide
          </h1>
          <Sparkles className="w-6 h-6 text-purple-400" />
        </div>
        <p className="text-purple-200 text-sm max-w-sm mx-auto">
          They'll be with you through modules, tools, and whenever you need support.
          You can change your mind anytime in Settings.
        </p>
      </motion.div>

      {/* Character Card */}
      <div className="relative w-full max-w-sm">
        {/* Navigation Arrows */}
        <button
          onClick={goPrev}
          className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 sm:-translate-x-4 z-10 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
          aria-label="Previous character"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>

        <button
          onClick={goNext}
          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 sm:translate-x-4 z-10 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-400"
          aria-label="Next character"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>

        {/* Character Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentCharacter.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.2 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            {/* Avatar */}
            <div className="flex justify-center mb-4">
              <motion.div
                className="w-32 h-32 rounded-full overflow-hidden border-4 bg-slate-800"
                style={{ borderColor: currentCharacter.accentColor }}
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                {/* Placeholder until images exist */}
                <div
                  className="w-full h-full flex items-center justify-center text-4xl font-bold text-white"
                  style={{ backgroundColor: currentCharacter.accentColor + '40' }}
                >
                  {currentCharacter.name[0]}
                </div>
                {/* Uncomment when images exist:
                <img
                  src={currentCharacter.avatar}
                  alt={currentCharacter.avatarAlt}
                  className="w-full h-full object-cover"
                />
                */}
              </motion.div>
            </div>

            {/* Name & Tagline */}
            <div className="text-center mb-4">
              <h2 className="text-2xl font-bold text-white mb-1">
                {currentCharacter.name}
              </h2>
              <p
                className="text-sm font-medium"
                style={{ color: currentCharacter.accentColor }}
              >
                {currentCharacter.tagline}
              </p>
            </div>

            {/* Description */}
            <p className="text-purple-100 text-center text-sm mb-6">
              {currentCharacter.description}
            </p>

            {/* Sample Voice */}
            <div className="bg-black/20 rounded-xl p-4 mb-4">
              <p className="text-purple-300 text-xs mb-2 uppercase tracking-wide font-medium">
                How {currentCharacter.name} talks:
              </p>
              <p className="text-white text-sm italic">
                "{currentCharacter.personality.openings[0]}"
              </p>
            </div>

            {/* Personality Traits */}
            <div className="flex flex-wrap gap-2 justify-center">
              {currentCharacter.personality.style.split(' and ').map((trait, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 rounded-full text-xs font-medium"
                  style={{
                    backgroundColor: currentCharacter.accentColor + '30',
                    color: currentCharacter.accentColor
                  }}
                >
                  {trait.trim()}
                </span>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-4">
          {characterOrder.map((id, index) => (
            <button
              key={id}
              onClick={() => setCurrentIndex(index)}
              className={`w-2.5 h-2.5 rounded-full transition-all duration-200 ${
                index === currentIndex
                  ? 'bg-white scale-110'
                  : 'bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Go to ${allCharacters[id].name}`}
            />
          ))}
        </div>
      </div>

      {/* Select Button */}
      <motion.button
        onClick={handleSelect}
        disabled={isSelecting}
        className="mt-8 px-8 py-4 bg-white text-purple-900 rounded-2xl font-bold text-lg hover:bg-purple-100 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-4 focus:ring-purple-400/50"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        style={{
          boxShadow: `0 0 30px ${currentCharacter.accentColor}40`
        }}
      >
        {isSelecting ? (
          <>
            <motion.div
              className="w-5 h-5 border-2 border-purple-900 border-t-transparent rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
            />
            Selecting...
          </>
        ) : (
          <>
            <Check className="w-5 h-5" />
            Choose {currentCharacter.name}
          </>
        )}
      </motion.button>

      {/* Skip / Keep Current (for settings) */}
      {!isOnboarding && (
        <button
          onClick={() => onComplete?.(null)}
          className="mt-4 text-purple-300 text-sm hover:text-white transition-colors"
        >
          Keep current guide
        </button>
      )}

      {/* Character Counter */}
      <p className="mt-6 text-purple-400 text-xs">
        {currentIndex + 1} of {characterOrder.length} guides
      </p>
    </div>
  );
};

export default GuideSelection;
